import { apiPostJson } from "@lyder-talk/core/lib/api-client";
import { useCallback, useEffect, useMemo, useRef, useState, createContext, useContext, ReactNode } from "react";
import { useStreamObject } from "@/hooks/use-stream-object";
import {
    questionsResponseSchema,
    vocabularyResponseSchema,
    speakingGuideResponseSchema,
} from "@/lib/schemas/topic-schemas";
import type { Question, Vocabulary, SpeakingGuide, ProgressStep } from "@/lib/topic/types";
import { mergeProgressSteps } from "@/lib/topic/progress";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useTopicGenerationStatusContext } from "@/contexts/TopicGenerationStatusContext";
import type { Json } from "@/integrations/supabase/types";
import type { SpeakingGuideData } from "@/hooks/use-topic-detail-generation";
import { parseAIChatStreamLine, splitBufferedLines } from "@/lib/ai-chat/stream-parser";
import { auth } from "@lyder-talk/core/lib/auth-client";
import { db } from "@lyder-talk/core/lib/db-client";

// --- Types ---

export type { Question, Vocabulary } from "@/lib/topic/types";

export type StreamingPhase = "questions" | "vocabulary" | "speaking-guide" | "generating-all" | null;

export type { ProgressStep };

interface ContentGenerationContextType {
    // State
    loading: boolean;
    questions: Question[];
    vocabulary: Vocabulary[];
    speakingGuideData: SpeakingGuideData | undefined;
    speakingGuideLoading: boolean;
    streamingPhase: StreamingPhase;
    hasGenerated: boolean;
    currentSessionId: string | null;
    currentTopic: string | null;
    currentSlug: string | null;
    progressSteps: ProgressStep[];
    isParallelGenerating: boolean;
    currentCategory: string | null;
    activeContentTypes: Array<'questions' | 'vocabulary' | 'speaking-guide'>;

    // Actions
    generateContent: (sessionId: string, topic: string, slug?: string | null) => void;
    generateQuestionsOnly: (sessionId: string, topic: string, slug?: string | null) => void;
    generateVocabularyOnly: (sessionId: string, topic: string, slug?: string | null) => void;
    generateSpeakingGuideOnly: (sessionId: string, topic: string, slug?: string | null) => void;
    generateContentTypes: (sessionId: string, topic: string, contentTypes: Array<'questions' | 'vocabulary' | 'speaking-guide'>, slug?: string | null) => void;
    loadExistingSpeakingGuide: (sessionId: string, data: SpeakingGuideData) => void;
    reset: () => void;
    clearForSession: (sessionId: string) => void;
    cancelGeneration: () => void;
}

// --- Constants ---

// Section generation now streams from the API (topic-draft endpoints).
const API_BASE = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "").replace(/\/+$/, "");

// --- Context ---

const ContentGenerationContext = createContext<ContentGenerationContextType | undefined>(undefined);

export const useContentGenerationContext = () => {
    const context = useContext(ContentGenerationContext);
    if (!context) {
        throw new Error("useContentGenerationContext must be used within a ContentGenerationProvider");
    }
    return context;
};

// --- Provider ---

export const ContentGenerationProvider = ({ children }: { children: ReactNode }) => {
    const { toast } = useToast();
    const { access_token } = useAuth();
    const { setStatus } = useTopicGenerationStatusContext();

    // Local state
    const [loading, setLoading] = useState(false);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [vocabulary, setVocabulary] = useState<Vocabulary[]>([]);
    const [streamingPhase, setStreamingPhase] = useState<StreamingPhase>(null);
    const [hasGenerated, setHasGenerated] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [currentTopic, setCurrentTopic] = useState<string | null>(null);
    const [currentSlug, setCurrentSlug] = useState<string | null>(null);
    const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
    const [isParallelGenerating, setIsParallelGenerating] = useState(false);
    const [currentCategory, setCurrentCategory] = useState<string | null>(null);
    const [activeContentTypes, setActiveContentTypes] = useState<Array<'questions' | 'vocabulary' | 'speaking-guide'>>([]);

    // Refs
    const topicRef = useRef<string>("");
    const sessionIdRef = useRef<string | null>(null);
    const slugRef = useRef<string | null>(null);
    const questionsDataRef = useRef<Question[]>([]);
    // Carry vocabulary + speaking guide forward through the chain (vocab → guide →
    // questions) so the questions step can feed them into its prompt.
    const vocabularyDataRef = useRef<Vocabulary[]>([]);
    const speakingGuideDataRef = useRef<SpeakingGuideData | undefined>(undefined);
    // Tracks parallel completion of vocabulary + speaking guide so that the
    // onFinish callbacks know when both have landed and it's safe to proceed
    // to questions.
    const parallelPhaseRef = useRef<{ vocabulary: boolean; speakingGuide: boolean } | null>(null);
    // Track the current generation to prevent race conditions when switching topics
    const generationIdRef = useRef<string | null>(null);
    // Track which content types are being regenerated in selective mode
    const activeContentTypesRef = useRef<Array<'questions' | 'vocabulary' | 'speaking-guide'>>([]);
    // Abort controllers for cancelling in-flight streams
    const abortControllerQuestionsRef = useRef<AbortController | null>(null);
    const abortControllerVocabularyRef = useRef<AbortController | null>(null);
    const abortControllerParallelRef = useRef<AbortController | null>(null);

    // Memoize headers to update when access_token changes.
    //
    // `Content-Type` is not a nicety here: the NestJS API parses JSON bodies with
    // express.json(), which ignores a request that does not declare it, so the
    // handler received `{}` and rejected every Quick Generate build with
    // "Topic cannot be empty" even though the payload on the wire was correct.
    // The Supabase edge functions read `req.json()`, which does not care. Every
    // request that goes out with this object has a JSON body.
    const headers = useMemo(() => {
        const h: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        if (access_token) {
            h['Authorization'] = `Bearer ${access_token}`;
        }
        return h;
    }, [access_token]);

    // --- Questions Hook ---

    const {
        object: questionsObject,
        submit: submitQuestions,
        isLoading: questionsLoading,
    } = useStreamObject({
        api: `${API_BASE}/v1/topic-drafts/questions`,
        schema: questionsResponseSchema,
        label: "questions",
        abortRef: abortControllerQuestionsRef,
        onFinish: async (object, error) => {
            // Capture the generation ID at the time the callback fires
            const currentGenerationId = generationIdRef.current;

            if (error) {
                // useStreamObject already showed the toast — just reset state
                if (generationIdRef.current === currentGenerationId) {
                    setLoading(false);
                    setStreamingPhase(null);
                }
                return;
            }

            // Verify this generation is still current before proceeding
            if (generationIdRef.current !== currentGenerationId) {
                console.log("Questions generation completed but user switched topics - discarding");
                return;
            }

            const obj = object as { questions?: Question[] } | undefined;
            if (!obj || !obj.questions || !Array.isArray(obj.questions) || obj.questions.length === 0) {
                toast({
                    title: "Warning",
                    description: "No questions were generated. Please try again.",
                    variant: "destructive",
                });
                if (generationIdRef.current === currentGenerationId) {
                    setLoading(false);
                    setStreamingPhase(null);
                }
                return;
            }

            const questionsData = obj.questions;
            questionsDataRef.current = questionsData;

            const targetSessionId = sessionIdRef.current;
            const targetTopic = topicRef.current;

            // Questions are the LAST link in the chain (vocab → guide → questions).
            // Save just the questions — an empty vocabulary array leaves the
            // already-saved vocabulary untouched (saveToDatabase guards on length).
            if (targetSessionId) {
                await saveToDatabase(targetSessionId, questionsData, []);
            }

            if (generationIdRef.current === currentGenerationId) {
                setHasGenerated(true);
                setLoading(false);
                setIsParallelGenerating(false);
                setStreamingPhase(null);
                setCurrentSessionId(null);
                setProgressSteps([
                    { step: "questions", message: `${questionsData.length} questions ready`, completed: true },
                    { step: "vocabulary", message: vocabularyDataRef.current.length ? `${vocabularyDataRef.current.length} words ready` : "Keeping existing", completed: true },
                    { step: "speaking-guide", message: speakingGuideDataRef.current ? "Speaking guide ready" : "Keeping existing", completed: true },
                ]);
                if (targetSessionId) {
                    await sendNotification(targetSessionId, targetTopic);
                }
                toast({
                    title: "Content Generated!",
                    description: `${questionsData.length} questions ready`,
                });
            }
        },
    });

    // --- Vocabulary Hook ---

    const {
        object: vocabularyObject,
        submit: submitVocabulary,
        isLoading: vocabularyLoading,
    } = useStreamObject({
        api: `${API_BASE}/v1/topic-drafts/vocabulary`,
        schema: vocabularyResponseSchema,
        label: "vocabulary",
        abortRef: abortControllerVocabularyRef,
        onFinish: async (object, error) => {
            // Capture the generation ID and session ID at the time the callback fires
            const currentGenerationId = generationIdRef.current;
            const targetSessionId = sessionIdRef.current;
            const targetTopic = topicRef.current;

            if (error) {
                // useStreamObject already showed the toast — just reset state
                if (generationIdRef.current === currentGenerationId) {
                    setLoading(false);
                    setStreamingPhase(null);
                }
                return;
            }

            // Verify this generation is still current before proceeding
            if (generationIdRef.current !== currentGenerationId) {
                console.log("Vocabulary generation completed but user switched topics - discarding");
                return;
            }

            const obj = object as { vocabulary?: Vocabulary[] } | undefined;
            if (!obj || !obj.vocabulary || !Array.isArray(obj.vocabulary) || obj.vocabulary.length === 0) {
                toast({
                    title: "Warning",
                    description: "No vocabulary was generated. Please try again.",
                    variant: "destructive",
                });
                if (generationIdRef.current === currentGenerationId) {
                    setLoading(false);
                    setStreamingPhase(null);
                }
                return;
            }

            const vocabularyData = obj.vocabulary;
            vocabularyDataRef.current = vocabularyData;

            // Vocabulary is the FIRST link in the chain — save just the
            // vocabulary (empty questions array leaves any existing questions
            // untouched; saveToDatabase guards each table on length).
            if (targetSessionId) {
                await saveToDatabase(targetSessionId, [], vocabularyData);
            }

            // Only continue the chain if this generation is still current
            if (generationIdRef.current === currentGenerationId) {
                const shouldGenSpeaking = activeContentTypesRef.current.includes('speaking-guide');
                const shouldGenQuestions = activeContentTypesRef.current.includes('questions');

                if (shouldGenSpeaking) {
                    // Check if the speaking guide was already kicked off in
                    // parallel (generateContentTypes started both at once).
                    const parallel = parallelPhaseRef.current;
                    if (parallel) {
                        // Speaking guide is running in parallel — mark vocab done
                        // and check if both have finished.
                        parallel.vocabulary = true;
                        if (parallel.speakingGuide) {
                            // Both done — proceed to questions (if requested).
                            parallelPhaseRef.current = null;
                            if (shouldGenQuestions) {
                                setStreamingPhase("questions");
                                setProgressSteps([
                                    { step: "questions", message: "Generating questions...", completed: false },
                                    { step: "vocabulary", message: `${vocabularyData.length} words ready`, completed: true },
                                    { step: "speaking-guide", message: "Speaking guide ready", completed: true },
                                ]);
                                submitQuestions({
                                    topic: targetTopic,
                                    vocabulary: vocabularyData.map(v => ({ word: v.word, definition: v.definition })),
                                    speakingGuide: speakingGuideDataRef.current,
                                });
                            } else {
                                // Vocabulary + speaking guide only — terminal.
                                setHasGenerated(true);
                                setLoading(false);
                                setIsParallelGenerating(false);
                                setStreamingPhase(null);
                                setCurrentSessionId(null);
                                setProgressSteps([
                                    { step: "questions", message: "Keeping existing", completed: true },
                                    { step: "vocabulary", message: `${vocabularyData.length} words ready`, completed: true },
                                    { step: "speaking-guide", message: "Speaking guide ready", completed: true },
                                ]);
                                if (targetSessionId) {
                                    await sendNotification(targetSessionId, targetTopic);
                                }
                            }
                        }
                    } else {
                        // Sequential fallback: guide wasn't started in parallel.
                        setStreamingPhase("speaking-guide");
                        setProgressSteps([
                            { step: "questions", message: shouldGenQuestions ? "Waiting..." : "Keeping existing", completed: !shouldGenQuestions },
                            { step: "vocabulary", message: `${vocabularyData.length} words ready`, completed: true },
                            { step: "speaking-guide", message: "Generating speaking guide...", completed: false },
                        ]);
                        fetchSpeakingGuideForTopic(targetTopic, vocabularyData);
                    }
                } else if (shouldGenQuestions) {
                    // No speaking guide in this run — go straight to questions,
                    // feeding in the fresh vocabulary + any existing guide.
                    setStreamingPhase("questions");
                    setProgressSteps([
                        { step: "questions", message: "Generating questions...", completed: false },
                        { step: "vocabulary", message: `${vocabularyData.length} words ready`, completed: true },
                        { step: "speaking-guide", message: speakingGuideDataRef.current ? "Speaking guide ready" : "Keeping existing", completed: true },
                    ]);
                    submitQuestions({
                        topic: targetTopic,
                        vocabulary: vocabularyData.map(v => ({ word: v.word, definition: v.definition })),
                        speakingGuide: speakingGuideDataRef.current,
                    });
                } else {
                    // Vocabulary-only regeneration — terminal.
                    setHasGenerated(true);
                    setLoading(false);
                    setIsParallelGenerating(false);
                    setStreamingPhase(null);
                    setCurrentSessionId(null);
                    setProgressSteps([
                        { step: "questions", message: "Keeping existing", completed: true },
                        { step: "vocabulary", message: `${vocabularyData.length} words ready`, completed: true },
                        { step: "speaking-guide", message: "Keeping existing", completed: true },
                    ]);
                    toast({
                        title: "Content Generated!",
                        description: `${vocabularyData.length} vocabulary items ready`,
                    });
                }
            }
        },
    });

    // --- Speaking Guide Hook ---

    const [speakingGuideData, setSpeakingGuideData] = useState<SpeakingGuideData | undefined>(undefined);

    // Track if we have successfully received content to suppress spurious errors
    const speakingGuideHasContentRef = useRef(false);

    const {
        object: speakingGuideObject,
        submit: submitSpeakingGuide,
        isLoading: speakingGuideLoading,
        stop: stopSpeakingGuide,
    } = useStreamObject({
        api: `${API_BASE}/v1/topic-drafts/speaking-guide`,
        schema: speakingGuideResponseSchema,
        label: "speaking guide",
        onFinish: async (object, error) => {
            // Capture the generation ID and session ID at the time the callback fires
            const currentGenerationId = generationIdRef.current;
            const targetSessionId = sessionIdRef.current;
            const targetTopic = topicRef.current;

            if (error) {
                // If we already have content generated, suppress state reset
                if (speakingGuideHasContentRef.current) {
                    if (generationIdRef.current === currentGenerationId) {
                        setStreamingPhase(null);
                    }
                    return;
                }

                // useStreamObject already showed the toast — just reset state
                if (generationIdRef.current === currentGenerationId) {
                    setLoading(false);
                    setIsParallelGenerating(false);
                    setStreamingPhase(null);
                    setCurrentSessionId(null);
                }
                return;
            }

            speakingGuideHasContentRef.current = true;

            const data = object as SpeakingGuideData | undefined;
            if (data) {
                speakingGuideDataRef.current = data;

                // Verify this generation is still current before saving
                if (generationIdRef.current === currentGenerationId) {
                    if (targetSessionId) {
                        await saveSpeakingGuide(targetSessionId, data);
                    }

                    const shouldGenQuestions = activeContentTypesRef.current.includes('questions');
                    // Check if vocabulary was started in parallel.
                    const parallel = parallelPhaseRef.current;
                    if (parallel) {
                        parallel.speakingGuide = true;
                        if (parallel.vocabulary) {
                            // Both done — proceed to questions.
                            parallelPhaseRef.current = null;
                            if (shouldGenQuestions) {
                                setStreamingPhase("questions");
                                setProgressSteps([
                                    { step: "questions", message: "Generating questions...", completed: false },
                                    { step: "vocabulary", message: vocabularyDataRef.current.length ? `${vocabularyDataRef.current.length} words ready` : "Keeping existing", completed: true },
                                    { step: "speaking-guide", message: "Speaking guide ready", completed: true },
                                ]);
                                submitQuestions({
                                    topic: targetTopic,
                                    vocabulary: vocabularyDataRef.current.map(v => ({ word: v.word, definition: v.definition })),
                                    speakingGuide: data,
                                });
                            } else {
                                // both regenerated but no questions — terminal.
                                setHasGenerated(true);
                                setLoading(false);
                                setIsParallelGenerating(false);
                                setStreamingPhase(null);
                                setCurrentSessionId(null);
                                setProgressSteps([
                                    { step: "questions", message: "Keeping existing", completed: true },
                                    { step: "vocabulary", message: `${vocabularyDataRef.current.length} words ready`, completed: true },
                                    { step: "speaking-guide", message: "Speaking guide ready", completed: true },
                                ]);
                                if (targetSessionId) {
                                    await sendNotification(targetSessionId, targetTopic);
                                }
                            }
                        }
                    } else if (shouldGenQuestions) {
                        // Questions run last, fed by the fresh vocabulary + speaking guide.
                        setStreamingPhase("questions");
                        setProgressSteps([
                            { step: "questions", message: "Generating questions...", completed: false },
                            { step: "vocabulary", message: vocabularyDataRef.current.length ? `${vocabularyDataRef.current.length} words ready` : "Keeping existing", completed: true },
                            { step: "speaking-guide", message: "Speaking guide ready", completed: true },
                        ]);
                        submitQuestions({
                            topic: targetTopic,
                            vocabulary: vocabularyDataRef.current.map(v => ({ word: v.word, definition: v.definition })),
                            speakingGuide: data,
                        });
                    } else {
                        // Speaking-guide-only regeneration — terminal.
                        setHasGenerated(true);
                        setLoading(false);
                        setIsParallelGenerating(false);
                        setStreamingPhase(null);
                        setCurrentSessionId(null);
                        setProgressSteps([
                            { step: "questions", message: "Keeping existing", completed: true },
                            { step: "vocabulary", message: "Keeping existing", completed: true },
                            { step: "speaking-guide", message: "Speaking guide ready", completed: true },
                        ]);
                        if (targetSessionId) {
                            await sendNotification(targetSessionId, targetTopic);
                        }
                    }
                }
            } else if (generationIdRef.current === currentGenerationId) {
                setLoading(false);
                setIsParallelGenerating(false);
                setStreamingPhase(null);
                setCurrentSessionId(null);
            }
        },
    });

    const resetSpeakingGuide = useCallback(() => {
        speakingGuideHasContentRef.current = false;
        setSpeakingGuideData(undefined);
        stopSpeakingGuide();
    }, [stopSpeakingGuide]);

    // Sync speaking guide data
    useEffect(() => {
        if (speakingGuideObject) {
            setSpeakingGuideData(speakingGuideObject as SpeakingGuideData);
        }
    }, [speakingGuideObject]);

    // --- Sync Effects ---

    useEffect(() => {
        const obj = questionsObject as { questions?: Question[] } | undefined;
        if (obj?.questions && Array.isArray(obj.questions)) {
            setQuestions(obj.questions);
        }
    }, [questionsObject]);

    useEffect(() => {
        const obj = vocabularyObject as { vocabulary?: Vocabulary[] } | undefined;
        if (obj?.vocabulary && Array.isArray(obj.vocabulary)) {
            setVocabulary(obj.vocabulary);
        }
    }, [vocabularyObject]);

    // Auto-clear streaming phase when loading finishes
    useEffect(() => {
        const isAnyLoading = loading || questionsLoading || vocabularyLoading || speakingGuideLoading;
        if (!isAnyLoading && streamingPhase) {
            const timer = setTimeout(() => {
                setStreamingPhase(null);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [loading, questionsLoading, vocabularyLoading, speakingGuideLoading, streamingPhase]);

    // Sync status to the global status context for header display
    useEffect(() => {
        const isLoading = questionsLoading || vocabularyLoading || loading;
        setStatus(streamingPhase as any, isLoading, currentSlug, currentSessionId, currentTopic);
    }, [streamingPhase, questionsLoading, vocabularyLoading, loading, currentSlug, currentSessionId, currentTopic, setStatus]);

    // --- Database Functions ---

    const saveToDatabase = useCallback(async (
        targetSessionId: string,
        questionsData: Question[],
        vocabularyData: Vocabulary[],
        category?: string | null
    ): Promise<void> => {
        try {
            const { data: { user } } = await auth.getUser();
            if (!user) {
                console.error("User not authenticated");
                return;
            }

            if (category) {
                console.log(`[saveToDatabase] Looking up category: "${category}"`);
                const { data: categoryData, error: categoryError } = await db.from("categories")
                    .select("id")
                    .ilike("name", category)
                    .maybeSingle();

                if (categoryError) {
                    console.error(`[saveToDatabase] Error looking up category:`, categoryError);
                }

                if (categoryData?.id) {
                    console.log(`[saveToDatabase] Found category ID: ${categoryData.id}`);
                    const { error: updateError } = await db.from("practice_sessions")
                        .update({ category_id: categoryData.id })
                        .eq("id", targetSessionId)
                        .eq("user_id", user.id);
                    if (updateError) {
                        console.error("Error updating session category:", updateError);
                    }
                } else {
                    console.warn(`[saveToDatabase] Category "${category}" not found in database`);
                }
            }

            // Delete existing questions and vocabulary, then insert new ones
            if (questionsData.length > 0) {
                await db.from("questions")
                    .delete()
                    .eq("practice_session_id", targetSessionId);

                const { error: qError } = await db.from("questions")
                    .insert(questionsData.map(q => ({
                        practice_session_id: targetSessionId,
                        question: q.question,
                        difficulty: q.difficulty,
                        step_by_step: q.stepByStep || [],
                        example_answer: q.exampleAnswer || null,
                        key_points: q.keyPoints || [],
                    })));

                if (qError) {
                    console.error("Error inserting questions:", qError);
                }
            }

            if (vocabularyData.length > 0) {
                await db.from("vocabulary")
                    .delete()
                    .eq("practice_session_id", targetSessionId);

                const { error: vError } = await db.from("vocabulary")
                    .insert(vocabularyData.map(v => ({
                        practice_session_id: targetSessionId,
                        word: v.word,
                        definition: v.definition,
                        example: v.example,
                        ipa: v.ipa || null,
                        type: v.type || null,
                        cert_level: v.certLevel || null,
                    })));

                if (vError) {
                    console.error("Error inserting vocabulary:", vError);
                }
            }
        } catch (error) {
            console.error("Database error in saveToDatabase:", error);
        }
    }, []);

    const saveSpeakingGuide = useCallback(async (
        targetSessionId: string,
        data: SpeakingGuideData
    ): Promise<void> => {
        try {
            const { data: { user } } = await auth.getUser();
            if (!user) return;

            const { error } = await db.from("practice_sessions")
                .update({
                    speaking_guide: data as unknown as Json, // Store in speaking_guide column
                })
                .eq("id", targetSessionId)
                .eq("user_id", user.id);

            if (error) console.error("Error saving speaking guide:", error);
        } catch (error) {
            console.error("Error in saveSpeakingGuide:", error);
        }
    }, []);

    const sendNotification = useCallback(async (targetSessionId: string, topicName: string) => {
        try {
            const { data: { user } } = await auth.getUser();
            if (!user) return;

            const payload = {
                notification_type: 'topic_generated',
                study_group_id: 'none',
                practice_session_id: targetSessionId,
                session_topic: topicName,
                user_id: user.id,
            };

            await apiPostJson('/v1/notifications/push', payload);
        } catch (error) {
            console.error("Failed to send notification:", error);
        }
    }, []);

    // --- Public Actions ---

    const generateContent = useCallback(async (sessionId: string, topic: string, slug?: string | null) => {
        if (streamingPhase || isParallelGenerating) {
            return;
        }

        if (!access_token) {
            toast({
                title: "Authentication Required",
                description: "Please sign in to generate content.",
                variant: "destructive",
            });
            return;
        }

        const newGenerationId = `${sessionId}-${Date.now()}`;
        generationIdRef.current = newGenerationId;

        setLoading(true);
        setIsParallelGenerating(true);
        setStreamingPhase("generating-all");
        setCurrentSessionId(sessionId);
        setCurrentTopic(topic);
        setCurrentSlug(slug || null);

        topicRef.current = topic;
        sessionIdRef.current = sessionId;
        slugRef.current = slug || null;
        questionsDataRef.current = [];
        vocabularyDataRef.current = [];
        speakingGuideDataRef.current = undefined;
        activeContentTypesRef.current = ['questions', 'vocabulary', 'speaking-guide'];
        setActiveContentTypes(['questions', 'vocabulary', 'speaking-guide']);

        setQuestions([]);
        setVocabulary([]);
        setSpeakingGuideData(undefined);
        speakingGuideHasContentRef.current = false;

        setProgressSteps([
            { step: "questions", message: "Waiting...", completed: false },
            { step: "vocabulary", message: "Generating vocabulary...", completed: false },
            { step: "speaking-guide", message: "Creating speaking guide...", completed: false },
        ]);

        try {
            abortControllerParallelRef.current = new AbortController();
            // One entry point for building a topic. The Quick Generate build and
            // the chat's `createCompleteTopic` tool both run the same generator
            // server-side; this is the deterministic one.
            const response = await fetch(`${API_BASE}/v1/topic-drafts`, {
                method: "POST",
                headers,
                body: JSON.stringify({ topic }),
                signal: abortControllerParallelRef.current.signal,
            });

            if (generationIdRef.current !== newGenerationId) {
                console.log("Generation completed but user switched topics - discarding");
                return;
            }

            if (!response.ok || !response.body) {
                const errorData = await response.json().catch(() => ({} as Record<string, unknown>));
                // The API's error contract is `{ code, message }`
                // (DomainExceptionFilter). `error` is the older Supabase function
                // shape, kept as a fallback. Reading only `error` is what turned
                // "Topic cannot be empty" into a bare HTTP 400 in the toast.
                const detail =
                    typeof errorData.message === "string" && errorData.message
                        ? errorData.message
                        : typeof errorData.error === "string" && errorData.error
                            ? errorData.error
                            : null;
                throw new Error(detail ?? `HTTP ${response.status}: Failed to generate content`);
            }

            // Consume the SSE stream — progress events update the UI in
            // real-time; the final data-topic-content event carries the result.
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let resultData: { title?: string; description?: string; category: string; bannerUrl?: string; bannerAlt?: string; questions: Question[]; vocabulary: Vocabulary[]; speakingGuide: SpeakingGuideData } | null = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                if (generationIdRef.current !== newGenerationId) {
                    console.log("Generation completed but user switched topics - discarding");
                    reader.cancel();
                    return;
                }

                const decodedChunk = decoder.decode(value, { stream: true });
                const { lines, buffer: newBuffer } = splitBufferedLines(buffer, decodedChunk);
                buffer = newBuffer;

                for (const line of lines) {
                    const parsedEvents = parseAIChatStreamLine(line);
                    for (const parsedEvent of parsedEvents) {
                        if (parsedEvent.type === "progress") {
                            // Map the stream parser's ProgressStep (which includes
                            // "topic"/"complete"/"error") to our local 3-step model.
                            const p = parsedEvent.progress;
                            if (p.step === "vocabulary" || p.step === "speaking-guide" || p.step === "questions") {
                                setProgressSteps((prev) => mergeProgressSteps(prev, {
                                    step: p.step as "questions" | "vocabulary" | "speaking-guide",
                                    message: p.message,
                                    completed: p.completed,
                                }));
                            }
                        } else if (parsedEvent.type === "topic-content") {
                            resultData = parsedEvent.data as typeof resultData;
                        } else if (parsedEvent.type === "error") {
                            throw new Error(parsedEvent.message);
                        }
                    }
                }
            }

            if (generationIdRef.current !== newGenerationId) {
                return;
            }

            if (!resultData || !resultData.questions || !resultData.vocabulary || !resultData.speakingGuide) {
                throw new Error("Incomplete response from generation service");
            }

            const data = resultData;
            setCurrentCategory(data.category || null);

            setQuestions(data.questions);
            setVocabulary(data.vocabulary);
            setSpeakingGuideData(data.speakingGuide);
            questionsDataRef.current = data.questions;

            setProgressSteps([
                { step: "questions", message: `${data.questions.length} questions ready`, completed: true },
                { step: "vocabulary", message: `${data.vocabulary.length} words ready`, completed: true },
                { step: "speaking-guide", message: "Speaking guide ready", completed: true },
            ]);

            await saveToDatabase(sessionId, data.questions, data.vocabulary, data.category);
            await saveSpeakingGuide(sessionId, data.speakingGuide);

            // Save auto-selected banner URL if the generation provided one
            if (data.bannerUrl) {
                try {
                    const { data: { user } } = await auth.getUser();
                    if (user) {
                        await db.from("practice_sessions")
                            .update({ banner_url: data.bannerUrl })
                            .eq("id", sessionId)
                            .eq("user_id", user.id);
                    }
                } catch (bannerErr) {
                    console.warn("Failed to save auto-selected banner URL:", bannerErr);
                }
            }

            await sendNotification(sessionId, topic);

            setHasGenerated(true);
            setLoading(false);
            setIsParallelGenerating(false);
            setStreamingPhase(null);
            setCurrentSessionId(null);

            toast({
                title: "Content Generated!",
                description: `${data.questions.length} questions, ${data.vocabulary.length} vocabulary items, and speaking guide ready`,
            });
        } catch (error) {
            if (generationIdRef.current !== newGenerationId) {
                return;
            }

            console.error("Parallel generation error:", error);
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "Failed to generate content. Please try again.",
                variant: "destructive",
            });

            setLoading(false);
            setIsParallelGenerating(false);
            setStreamingPhase(null);
            setCurrentSessionId(null);
            setProgressSteps([]);
        }
    }, [streamingPhase, isParallelGenerating, access_token, toast, headers]);

    const fetchSpeakingGuideForTopic = useCallback(async (
        topicName: string,
        vocabulariesData: Vocabulary[]
    ) => {
        const { data: { session } } = await auth.getSession();
        if (!session?.access_token) {
            toast({
                title: "Authentication Required",
                description: "Please sign in to generate speaking guide.",
                variant: "destructive",
            });
            setStreamingPhase(null);
            return;
        }

        // Reset content tracking
        speakingGuideHasContentRef.current = false;

        try {
            // In the chain the guide runs before questions, so only vocabulary
            // context is available/needed here.
            submitSpeakingGuide({
                topic: topicName.trim(),
                vocabularies: vocabulariesData.map(v => ({ word: v.word })),
            });
        } catch (err) {
            console.error("Error in fetchSpeakingGuide:", err);
            setStreamingPhase(null);
        }
    }, [submitSpeakingGuide, toast]);

    const generateQuestionsOnly = useCallback(async (sessionId: string, topic: string, slug?: string | null) => {
        if (streamingPhase) {
            return;
        }

        // Check authentication before starting generation
        if (!access_token) {
            toast({
                title: "Authentication Required",
                description: "Please sign in to generate content.",
                variant: "destructive",
            });
            return;
        }

        // Create a unique generation ID for this generation session
        const newGenerationId = `${sessionId}-${Date.now()}`;
        generationIdRef.current = newGenerationId;
        activeContentTypesRef.current = ['questions'];
        setActiveContentTypes(['questions']);

        setLoading(true);
        setStreamingPhase("questions");
        setCurrentSessionId(sessionId);
        setCurrentTopic(topic);
        setCurrentSlug(slug || null);

        topicRef.current = topic;
        sessionIdRef.current = sessionId;
        slugRef.current = slug || null;
        questionsDataRef.current = [];

        // Reset only the questions — keep existing vocabulary + speaking guide.
        setQuestions([]);

        try {
            const { data: { user } } = await auth.getUser();
            if (!user) {
                toast({
                    title: "Authentication Required",
                    description: "Please sign in to generate content.",
                    variant: "destructive",
                });
                setLoading(false);
                setStreamingPhase(null);
                setCurrentSessionId(null);
                return;
            }

            // Load the topic's existing vocabulary + speaking guide so the new
            // example answers can naturally weave them in.
            const { data: sessionData, error } = await db.from("practice_sessions")
                .select(`
                  id,
                  speaking_guide,
                  vocabulary (
                    word,
                    definition,
                    example,
                    ipa,
                    type,
                    cert_level
                  )
                `)
                .eq("id", sessionId)
                .eq("user_id", user.id)
                .single();

            if (error) {
                console.error("Error fetching session:", error);
            }

            const rawVocabulary = (sessionData as any)?.vocabulary || [];
            const existingVocabulary: Vocabulary[] = rawVocabulary.map((v: any) => ({
                word: v.word,
                definition: v.definition,
                example: v.example,
                ipa: v.ipa,
                type: v.type,
                certLevel: v.cert_level,
            }));
            const existingGuide = ((sessionData as any)?.speaking_guide as SpeakingGuideData | null) || undefined;

            vocabularyDataRef.current = existingVocabulary;
            speakingGuideDataRef.current = existingGuide;
            if (existingVocabulary.length) setVocabulary(existingVocabulary);
            if (existingGuide) setSpeakingGuideData(existingGuide);

            submitQuestions({
                topic,
                vocabulary: existingVocabulary.map(v => ({ word: v.word, definition: v.definition })),
                speakingGuide: existingGuide,
            });
        } catch (err) {
            console.error("Error in generateQuestionsOnly:", err);
            toast({
                title: "Error",
                description: "An unexpected error occurred.",
                variant: "destructive",
            });
            setLoading(false);
            setStreamingPhase(null);
            setCurrentSessionId(null);
        }
    }, [streamingPhase, access_token, toast, submitQuestions]);

    const generateVocabularyOnly = useCallback((sessionId: string, topic: string, slug?: string | null) => {
        if (streamingPhase) {
            return;
        }

        // Check authentication before starting generation
        if (!access_token) {
            toast({
                title: "Authentication Required",
                description: "Please sign in to generate content.",
                variant: "destructive",
            });
            return;
        }

        // Create a unique generation ID for this generation session
        const newGenerationId = `${sessionId}-${Date.now()}`;
        generationIdRef.current = newGenerationId;
        activeContentTypesRef.current = ['vocabulary'];
        setActiveContentTypes(['vocabulary']);

        // Set loading state immediately so UI shows loading indicator
        setLoading(true);
        setStreamingPhase("vocabulary");
        setCurrentSessionId(sessionId);
        setCurrentTopic(topic);
        setCurrentSlug(slug || null);

        topicRef.current = topic;
        sessionIdRef.current = sessionId;
        slugRef.current = slug || null;
        vocabularyDataRef.current = [];

        // Reset vocabulary only — vocabulary is the first link in the chain and
        // generates from the topic alone.
        setVocabulary([]);

        submitVocabulary({ topic });
    }, [streamingPhase, access_token, toast, submitVocabulary]);

    const generateSpeakingGuideOnly = useCallback(async (sessionId: string, topic: string, slug?: string | null) => {
        if (streamingPhase) {
            return;
        }

        // Check authentication before starting generation
        if (!access_token) {
            toast({
                title: "Authentication Required",
                description: "Please sign in to generate content.",
                variant: "destructive",
            });
            return;
        }

        // Create a unique generation ID for this generation session
        const newGenerationId = `${sessionId}-${Date.now()}`;
        generationIdRef.current = newGenerationId;
        activeContentTypesRef.current = ['speaking-guide'];
        setActiveContentTypes(['speaking-guide']);

        // Set loading state immediately so UI shows loading indicator
        setLoading(true);
        setStreamingPhase("speaking-guide");
        setCurrentSessionId(sessionId);
        setCurrentTopic(topic);
        setCurrentSlug(slug || null);

        topicRef.current = topic;
        sessionIdRef.current = sessionId;
        slugRef.current = slug || null;

        // Reset speaking guide only
        resetSpeakingGuide();

        // Load the topic's existing vocabulary to inform the guide.
        try {
            const { data: { user } } = await auth.getUser();
            if (!user) {
                toast({
                    title: "Authentication Required",
                    description: "Please sign in to generate content.",
                    variant: "destructive",
                });
                setLoading(false);
                setStreamingPhase(null);
                setCurrentSessionId(null);
                return;
            }

            const { data: sessionData, error } = await db.from("practice_sessions")
                .select(`
                  id,
                  vocabulary (
                    word,
                    definition,
                    example,
                    ipa,
                    type,
                    cert_level
                  )
                `)
                .eq("id", sessionId)
                .eq("user_id", user.id)
                .single();

            if (error) {
                console.error("Error fetching session:", error);
            }

            const rawVocabulary = (sessionData as any)?.vocabulary || [];
            const existingVocabulary: Vocabulary[] = rawVocabulary.map((v: any) => ({
                word: v.word,
                definition: v.definition,
                example: v.example,
                ipa: v.ipa,
                type: v.type,
                certLevel: v.cert_level,
            }));

            vocabularyDataRef.current = existingVocabulary;
            if (existingVocabulary.length) setVocabulary(existingVocabulary);

            // Loading state will be cleared by the speaking guide onFinish
            fetchSpeakingGuideForTopic(topic, existingVocabulary);
        } catch (error) {
            console.error("Error in generateSpeakingGuideOnly:", error);
            toast({
                title: "Error",
                description: "An unexpected error occurred.",
                variant: "destructive",
            });
            setLoading(false);
            setStreamingPhase(null);
            setCurrentSessionId(null);
        }
    }, [streamingPhase, access_token, toast, resetSpeakingGuide, fetchSpeakingGuideForTopic]);

    const generateContentTypes = useCallback(async (sessionId: string, topic: string, contentTypes: Array<'questions' | 'vocabulary' | 'speaking-guide'>, slug?: string | null) => {
        if (streamingPhase || isParallelGenerating) {
            return;
        }

        if (!access_token) {
            toast({
                title: "Authentication Required",
                description: "Please sign in to generate content.",
                variant: "destructive",
            });
            return;
        }

        if (contentTypes.length === 0) {
            return;
        }

        const hasQuestions = contentTypes.includes('questions');
        const hasVocabulary = contentTypes.includes('vocabulary');
        const hasSpeakingGuide = contentTypes.includes('speaking-guide');

        if (hasQuestions && hasVocabulary && hasSpeakingGuide) {
            generateContent(sessionId, topic, slug);
            return;
        }

        const newGenerationId = `${sessionId}-${Date.now()}`;
        generationIdRef.current = newGenerationId;
        activeContentTypesRef.current = contentTypes;
        setActiveContentTypes(contentTypes);

        setLoading(true);
        setIsParallelGenerating(true);
        setStreamingPhase("generating-all");
        setCurrentSessionId(sessionId);
        setCurrentTopic(topic);
        setCurrentSlug(slug || null);

        topicRef.current = topic;
        sessionIdRef.current = sessionId;
        slugRef.current = slug || null;

        setProgressSteps([
            { step: "questions", message: hasQuestions ? "Generating questions..." : "Keeping existing", completed: !hasQuestions },
            { step: "vocabulary", message: hasVocabulary ? "Generating vocabulary..." : "Keeping existing", completed: !hasVocabulary },
            { step: "speaking-guide", message: hasSpeakingGuide ? "Creating speaking guide..." : "Keeping existing", completed: !hasSpeakingGuide },
        ]);

        if (hasQuestions) {
            setQuestions([]);
            questionsDataRef.current = [];
        }
        if (hasVocabulary) {
            setVocabulary([]);
        }
        if (hasSpeakingGuide) {
            resetSpeakingGuide();
        }

        try {
            const { data: { user } } = await auth.getUser();
            if (!user) {
                throw new Error("User not authenticated");
            }

            const { data: sessionData, error: sessionError } = await db.from("practice_sessions")
                .select(`
                  id,
                  speaking_guide,
                  questions (
                    question,
                    difficulty,
                    step_by_step,
                    example_answer,
                    key_points
                  ),
                  vocabulary (
                    word,
                    definition,
                    example,
                    ipa,
                    type,
                    cert_level
                  )
                `)
                .eq("id", sessionId)
                .eq("user_id", user.id)
                .single();

            if (sessionError) {
                throw sessionError;
            }

            const rawQuestions = (sessionData as any)?.questions || [];
            const rawVocabulary = (sessionData as any)?.vocabulary || [];
            const existingQuestions: Question[] = rawQuestions.map((q: any) => ({
                question: q.question,
                difficulty: q.difficulty,
                stepByStep: q.step_by_step,
                exampleAnswer: q.example_answer,
                keyPoints: q.key_points,
            }));
            const existingVocabulary: Vocabulary[] = rawVocabulary.map((v: any) => ({
                word: v.word,
                definition: v.definition,
                example: v.example,
                ipa: v.ipa,
                type: v.type,
                certLevel: v.cert_level,
            }));
            const existingGuide = ((sessionData as any)?.speaking_guide as SpeakingGuideData | null) || undefined;

            // Seed refs so steps that aren't being regenerated still provide
            // context (and are preserved). Regenerated pieces start empty.
            questionsDataRef.current = existingQuestions;
            vocabularyDataRef.current = hasVocabulary ? [] : existingVocabulary;
            speakingGuideDataRef.current = hasSpeakingGuide ? undefined : existingGuide;

            // Show the existing (non-regenerated) content in the UI.
            if (!hasQuestions && existingQuestions.length) setQuestions(existingQuestions);
            if (!hasVocabulary && existingVocabulary.length) setVocabulary(existingVocabulary);
            if (!hasSpeakingGuide && existingGuide) setSpeakingGuideData(existingGuide);

            // When both vocabulary and speaking guide are being regenerated,
            // start them in parallel (replaces the old vocab → guide → questions
            // cascade). Questions still wait for both to finish.
            const parallel = hasVocabulary && hasSpeakingGuide;
            if (parallel) {
                parallelPhaseRef.current = { vocabulary: false, speakingGuide: false };
            }

            if (hasVocabulary) {
                submitVocabulary({ topic });
            }
            if (hasSpeakingGuide) {
                // Always start the speaking guide when it's selected.
                // In parallel mode (vocab + guide) both kick off together;
                // otherwise the guide runs alone (or with questions, which
                // are then kicked off in the guide's onFinish).
                fetchSpeakingGuideForTopic(topic, existingVocabulary);
            } else if (hasQuestions && !hasVocabulary) {
                // Questions only (no vocab, no guide) — start questions directly.
                submitQuestions({
                    topic,
                    vocabulary: existingVocabulary.map(v => ({ word: v.word, definition: v.definition })),
                    speakingGuide: existingGuide,
                });
            }
        } catch (error) {
            console.error("Error in generateContentTypes:", error);
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "Failed to generate content. Please try again.",
                variant: "destructive",
            });
            setLoading(false);
            setIsParallelGenerating(false);
            setStreamingPhase(null);
            setCurrentSessionId(null);
            setProgressSteps([]);
        }
    }, [streamingPhase, isParallelGenerating, access_token, toast, generateContent, submitQuestions, submitVocabulary, resetSpeakingGuide, fetchSpeakingGuideForTopic]);

    const loadExistingSpeakingGuide = useCallback((sessionId: string, data: SpeakingGuideData) => {
        // Only load if there's no active generation, or if this session matches the generating session
        if (currentSessionId && currentSessionId !== sessionId) {
            console.log("Skipping loadExistingSpeakingGuide - generation in progress for different session");
            return;
        }

        if (data) {
            setSpeakingGuideData(data);
        }
    }, [currentSessionId]);

    const reset = useCallback(() => {
        setLoading(false);
        setIsParallelGenerating(false);
        setQuestions([]);
        setVocabulary([]);
        setStreamingPhase(null);
        setHasGenerated(false);
        setCurrentSessionId(null);
        setCurrentTopic(null);
        setCurrentSlug(null);
        setCurrentCategory(null);
        setProgressSteps([]);
        generationIdRef.current = null;
        setSpeakingGuideData(undefined);
        resetSpeakingGuide();
    }, [resetSpeakingGuide]);

    const clearForSession = useCallback((sessionId: string) => {
        // Only clear if this is the current session being generated
        if (currentSessionId === sessionId) {
            reset();
        }
    }, [currentSessionId, reset]);

    const cancelGeneration = useCallback(() => {
        // Invalidate current generation
        generationIdRef.current = null;

        // Abort in-flight requests
        stopSpeakingGuide();
        abortControllerQuestionsRef.current?.abort();
        abortControllerVocabularyRef.current?.abort();
        abortControllerParallelRef.current?.abort();

        // Reset state
        reset();
    }, [stopSpeakingGuide, reset]);

    const value = useMemo(() => ({
        // State
        loading: questionsLoading || vocabularyLoading || loading,
        questions,
        vocabulary,
        speakingGuideData,
        speakingGuideLoading,
        streamingPhase,
        hasGenerated,
        currentSessionId,
        currentTopic,
        currentSlug,
        progressSteps,
        isParallelGenerating,
        currentCategory,
        activeContentTypes,
        generateContent,
        generateQuestionsOnly,
        generateVocabularyOnly,
        generateSpeakingGuideOnly,
        generateContentTypes,
        loadExistingSpeakingGuide,
        reset,
        clearForSession,
        cancelGeneration,
    }), [
        questionsLoading,
        vocabularyLoading,
        loading,
        questions,
        vocabulary,
        speakingGuideData,
        speakingGuideLoading,
        streamingPhase,
        hasGenerated,
        currentSessionId,
        currentTopic,
        currentSlug,
        progressSteps,
        isParallelGenerating,
        currentCategory,
        activeContentTypes,
        generateContent,
        generateQuestionsOnly,
        generateVocabularyOnly,
        generateSpeakingGuideOnly,
        generateContentTypes,
        loadExistingSpeakingGuide,
        reset,
        clearForSession,
    ]);

    return (
        <ContentGenerationContext.Provider value={value}>
            {children}
        </ContentGenerationContext.Provider>
    );
};
